import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import styles from './user-details-form.module.scss';
import { DatePicker, SelectBox, TextInput } from '../design-system/forms';
import { Modal } from '../design-system/modal/modal.component';
import { useProfileOptions, useProfileRequirementSets } from 'shared/hooks/profile.hooks';
import {
  ALLOWED_PERMISSIONS_FOR_CHANGE,
  Lang,
  ROUTES,
  INTERNAL_ROUTING,
  USER_PERMISSIONS,
  EVENTS,
} from 'shared/constants';
import { camelCaseToWords, sortByAlphabet } from 'shared/utils';
import { Button, IconButton } from '../design-system/buttons';
import { Alert, Checkbox, Tag } from 'antd';
import _ from 'lodash';
import { Tooltip } from '../core';
import { LabelsSelector } from '../labels/labels-selector';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { getWorkspaces } from 'shared/store/selectors/workspace.selector';
import {
  isQualityMetricsEnabled,
  isWorkspaceRestrictionsEnabled,
} from 'shared/store/selectors/features.selector';
import {
  PROVIDER_IDENTIFICATION_MAX,
  PROVIDER_IDENTIFICATION_PROVIDERS,
  PROVIDER_IDENTIFICATION_VALUE_MAX,
} from 'shared/constants/quality-metric.constants';
import { isSsoEnforcementEnabled } from 'shared/store/selectors/organization.selector';
import { isRoot } from 'shared/store/selectors/auth.selector';
import analyticsService from 'src/helpers/analytics.service';
import { resolveErrorMessage, resolveProviderNameError } from './resolve-error-message';
import {
  DEFAULT_PROVIDER,
  addProviderNameToList,
  getProviderLabel,
  normalizeProviderKey,
  providerNameId,
  toProviderIdentificationsPayload,
  toProviderNamesList,
} from './provider-identification';
import { buildRequirementSchema } from './build-requirement-schema';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';

// Accept the full USER_PERMISSIONS set so editing a ROOT user doesn't fail
// validation on the initial form value. The dropdown options below stay scoped
// to ALLOWED_PERMISSIONS_FOR_CHANGE, so customer admins still can't promote
// anyone to ROOT through the UI — they can only preserve an existing ROOT
// value or downgrade. Server-side guard in `updateUser` blocks promotion-to-ROOT
// if bypassed.
const PERMISSIONS_SCHEMA = Yup.object().shape({
  permissions: Yup.string().oneOf(Object.values(USER_PERMISSIONS)).required('Required!'),
});

const PERMISSIONS_SCHEMA_OPTIONAL = Yup.object().shape({
  permissions: Yup.string().oneOf(Object.values(USER_PERMISSIONS)),
});

const PROFILE_SCHEMA = Yup.object().shape({
  email: Yup.array()
    .of(Yup.string().email('Please insert a valid email'))
    .min(1, 'Add one or multiple emails separated by a comma or a space')
    .required('Required!'),
  firstName: Yup.string().nullable(),
  lastName: Yup.string(),
  phone: Yup.string().nullable(),
  pager: Yup.string().nullable(),
  cisco: Yup.string().nullable(),
  profession: Yup.string(),
  // role / levelOfTraining requiredness is applied dynamically in the component
  // via useProfileRequirementSets (needs the fetched per-org fields).
  role: Yup.string().nullable(),
  levelOfTraining: Yup.string().nullable(),
  department: Yup.string().nullable(),
  rotation: Yup.string().nullable(),
  division: Yup.array().of(Yup.string()).nullable(),
  location: Yup.array().of(Yup.string()).nullable(),
  specialty: Yup.array().of(Yup.string()).nullable(),
  unit: Yup.array().of(Yup.string()).nullable(),
  title: Yup.string().nullable(),
  restrictedWorkspaces: Yup.array().of(Yup.string()).nullable(),
  deactivationDate: Yup.date().nullable(),
});

const buildProviderNamesSchema = lang =>
  Yup.object().shape({
    providerNames: Yup.array()
      .of(
        Yup.object().shape({
          provider: Yup.string(),
          value: Yup.string()
            .trim()
            .max(
              PROVIDER_IDENTIFICATION_VALUE_MAX,
              lang.VALIDATION_PROVIDER_NAME_LENGTH?.replace(
                '{max}',
                String(PROVIDER_IDENTIFICATION_VALUE_MAX),
              ),
            ),
        }),
      )
      .max(
        PROVIDER_IDENTIFICATION_MAX,
        lang.VALIDATION_PROVIDER_NAMES_COUNT?.replace('{max}', String(PROVIDER_IDENTIFICATION_MAX)),
      ),
  });

/** Order-free fingerprint of a names list, compared per provider on the normalized key. */
const providerNamesFingerprint = names =>
  toProviderNamesList(names).map(providerNameId).sort().join('\n');

// Item errors come back as `[{ value: message }]`; the field shows one line.
const providerNamesErrorText = error =>
  Array.isArray(error) ? error.find(item => item?.value)?.value : error;

const PROVIDER_OPTIONS = Object.values(PROVIDER_IDENTIFICATION_PROVIDERS).map(provider => ({
  value: provider,
  label: getProviderLabel(provider),
}));

// Locked while adding several users at once — they can only differ per person.
const PER_PERSON_FIELDS = ['firstName', 'lastName', 'phone', 'pager', 'cisco', 'location', 'title'];

const permissionsOptions = Object.values(ALLOWED_PERMISSIONS_FOR_CHANGE)?.map(value => ({
  value: value,
  label: camelCaseToWords(value),
}));

const UserDetailsFormComponent = React.memo(function UserDetailsFormComponent({
  user,
  lang = Lang.USERS_MANAGEMENT,
  onDeleteUser,
  createUsers,
  updateUser,
  onFinish,
  openNotification,
  onTransferOwnership,
  canEdit = true,
  isBulkUpdate = false,
  allowToBulkUpdatePermissions = false,
  isAccountAdmin,
  manageRestrictedWorkspacesAccess,
}) {
  const isNewUser = !user?.id && !isBulkUpdate;
  const { professional = {}, ...userData } = user || {};

  const [error, setError] = useState(null);
  // The 409 on the provider names: shown while the list still holds the name (or is unchanged).
  const [takenProviderName, setTakenProviderName] = useState(null);
  // The add dialog: whether it is open, the provider the name is reported by, and the name typed.
  const [isAddProviderOpen, setAddProviderOpen] = useState(false);
  const [newNameProvider, setNewNameProvider] = useState(DEFAULT_PROVIDER);
  const [newName, setNewName] = useState('');
  const [isLoading, setLoading] = useState(false);
  const triggerElementRef = useRef(null);
  const isSsoEnforced = useSelector(isSsoEnforcementEnabled);
  // ROOT only: provider names map a clinician onto the identifiers an external system (MPOG,
  // Keck, GWU) reports them under, and a wrong one silently attributes another clinician's
  // records. Users management itself is open to ACCOUNT_ADMIN, so the feature flag alone is not
  // enough of a gate — the server is the real guard, this keeps the field out of their drawer.
  const isUserRoot = useSelector(isRoot);
  const isQualityMetricsVisible = useSelector(isQualityMetricsEnabled) && isUserRoot;
  const isSingleUserEdit = !isNewUser && !isBulkUpdate;
  const initialProviderNames = isSingleUserEdit
    ? toProviderNamesList(user?.providerIdentifications)
    : [];

  /** `true` when the save failed on a taken provider name, which is reported on the field. */
  const reportTakenProviderName = useCallback(
    (res, data) => {
      const taken = resolveProviderNameError(res, lang);
      if (!taken) {
        return false;
      }
      const sent = toProviderNamesList(data.providerIdentifications);
      setTakenProviderName({
        key: taken.name ? normalizeProviderKey(taken.name) : null,
        provider: taken.provider,
        message: taken.message,
        fingerprint: providerNamesFingerprint(sent),
      });
      setError(null);
      return true;
    },
    [lang],
  );

  const updateAndTransferOwnership = useCallback(
    data => {
      // Store the currently focused element before opening the modal
      triggerElementRef.current = document.activeElement;

      // Transfer ownership
      const transferOwnership = async ({ altOwnerId }) => {
        setLoading(true);
        const res = await updateUser({ id: user.id, userData: data, altOwnerId });
        if (reportTakenProviderName(res, data)) {
          setLoading(false);
          return;
        }
        const errorMessage = resolveErrorMessage(res, lang);

        if (errorMessage) {
          setError(errorMessage);
        } else {
          setError(null);
          onFinish();
        }

        setLoading(false);
      };

      const dismiss = () => {
        setLoading(false);
        // Restore focus to the trigger element after modal closes
        if (triggerElementRef.current) {
          // Use requestAnimationFrame to ensure modal is fully closed before restoring focus
          requestAnimationFrame(() => {
            triggerElementRef.current?.focus?.();
          });
        }
      };

      onTransferOwnership({ user, action: transferOwnership, dismiss });
    },
    [onFinish, onTransferOwnership, updateUser, user, lang, reportTakenProviderName],
  );

  const updateProfile = useCallback(
    async data => {
      setLoading(true);
      try {
        let res;
        if (isNewUser) {
          res = await createUsers({ userData: data });
          analyticsService.track(EVENTS.USER_ADD.ADD_SAVE);
        } else {
          // Check if the users permission have changed to viewer
          if (
            user.permissions !== data.permissions &&
            data.permissions === USER_PERMISSIONS.VIEWER
          ) {
            // Have to transfer ownership
            return updateAndTransferOwnership(data);
          }

          res = await updateUser({ id: user.id, userData: data });
          if (reportTakenProviderName(res, data)) {
            return;
          }
        }

        const errorMessage = resolveErrorMessage(res, lang);
        const savedUsers = res?.users?.length > 0;

        // Bulk add reports per-user failures alongside the users it did save — keep
        // going for those, and only hold the form open when nothing was saved.
        if (errorMessage && !savedUsers) {
          setError(errorMessage);
          return;
        }
        if (errorMessage) {
          openNotification({ message: errorMessage, type: 'error' });
        }

        if (
          data.restrictedWorkspaces?.length > 0 ||
          (user?.restrictedWorkspaces?.length > 0 && data.restrictedWorkspaces?.length === 0)
        ) {
          const wsAccessRes = await manageRestrictedWorkspacesAccess({
            userIds: isNewUser ? res.users.map(user => user.id) : data.id,
            workspaceIds: data.restrictedWorkspaces,
          });

          if (wsAccessRes?.error) {
            openNotification({
              message: lang.RESTRICTED_WORKSPACE_ERROR,
              type: 'error',
            });
          }
        }

        if (savedUsers) {
          openNotification({
            message: `${
              res.isNew ? lang.NOTIFICATION_CREATE_SUCCESS : lang.NOTIFICATION_UPDATE_SUCCESS
            }: ${res.users.map(u => u.email).join(', ')}`,
          });
          setError(null);
          onFinish();
        }
      } catch (error) {
        setError(lang.ERROR);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isNewUser,
      createUsers,
      updateUser,
      updateAndTransferOwnership,
      openNotification,
      lang,
      onFinish,
      reportTakenProviderName,
    ],
  );

  const { professionsWithRoles, rolesWithLevels, optionCodesByField } = useProfileRequirementSets();

  const requirementSchema = useMemo(
    () =>
      buildRequirementSchema({ professionsWithRoles, rolesWithLevels, optionCodesByField, lang }),
    [professionsWithRoles, rolesWithLevels, optionCodesByField, lang],
  );

  // base schema for signal user
  let validationSchema = PROFILE_SCHEMA.concat(requirementSchema)
    .concat(PERMISSIONS_SCHEMA)
    .concat(buildProviderNamesSchema(lang));
  if (isBulkUpdate) {
    validationSchema = PROFILE_SCHEMA.concat(requirementSchema);
  }
  if (allowToBulkUpdatePermissions) {
    PROFILE_SCHEMA.concat(PERMISSIONS_SCHEMA_OPTIONAL);
  }

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setValues,
    resetForm,
    values,
    errors,
    touched,
    isValid,
  } = useFormik({
    validationSchema,
    initialValues: isBulkUpdate
      ? {
          id: user?.map(user => user?.id),
          email: user?.map(user => user?.email),
        }
      : {
          ...userData,
          email: user?.email ? [user.email] : [],
          firstName: user?.firstName === user?.email ? undefined : user?.firstName,
          ...professional,
          permissions: user?.permissions ?? USER_PERMISSIONS.VIEWER,
          providerNames: initialProviderNames,
        },
    // The read `providerIdentifications` (with `key`) is never sent back as is.
    onSubmit: values =>
      updateProfile(toProviderIdentificationsPayload(values, initialProviderNames)),
  });

  const isProviderNameStillTaken =
    !!takenProviderName &&
    (takenProviderName.key
      ? (values.providerNames ?? []).some(
          ({ provider, value }) =>
            normalizeProviderKey(value) === takenProviderName.key &&
            (!takenProviderName.provider || provider === takenProviderName.provider),
        )
      : providerNamesFingerprint(values.providerNames) === takenProviderName.fingerprint);
  const providerNamesError = isProviderNameStillTaken
    ? takenProviderName.message
    : providerNamesErrorText(errors.providerNames);

  // Memoised: the `?? []` fallback would otherwise be a new array on every render, and both
  // callbacks below depend on it.
  const providerNames = useMemo(() => values.providerNames ?? [], [values.providerNames]);

  // The dialog always opens on an empty name, so a cancelled draft is never carried into the
  // next add. The provider is deliberately kept: several names of one provider are the common case.
  const openAddProviderName = useCallback(() => {
    setNewName('');
    setAddProviderOpen(true);
  }, []);

  const closeAddProviderName = useCallback(() => {
    setNewName('');
    setAddProviderOpen(false);
  }, []);

  // Appends the typed name under the picked provider. `addProviderNameToList` keeps the first
  // spelling of names that differ only in case or spacing, so re-adding one is a no-op — the row
  // is already in the table, which is why the dialog closes either way.
  const addProviderName = useCallback(() => {
    const value = newName.trim();
    if (!value) return;
    setFieldValue('providerNames', addProviderNameToList(providerNames, value, newNameProvider));
    closeAddProviderName();
  }, [setFieldValue, providerNames, newName, newNameProvider, closeAddProviderName]);

  const removeProviderName = useCallback(
    entry =>
      setFieldValue(
        'providerNames',
        providerNames.filter(name => providerNameId(name) !== providerNameId(entry)),
      ),
    [setFieldValue, providerNames],
  );

  // Several emails lock PER_PERSON_FIELDS; clear them, or the bulk create sends them to everyone.
  // One setValues: separate setFieldValue calls validate the stale email list and disable Add.
  const setEmails = useCallback(
    emails => {
      if (emails?.length > 1) {
        setValues(prev => ({
          ...prev,
          email: emails,
          ..._.fromPairs(PER_PERSON_FIELDS.map(field => [field, undefined])),
        }));
      } else {
        setFieldValue('email', emails);
      }
    },
    [setFieldValue, setValues],
  );

  const resetField = useCallback(
    field => {
      const newValues = { values };
      delete newValues.values[field];
      resetForm(newValues);
    },
    [values, resetForm],
  );
  const handleDependentFieldChange = useCallback(
    (field, dependentField, value) => {
      if (!value || value !== values[value]) {
        resetField(dependentField);
      }
      setFieldValue(field, value);
    },
    [setFieldValue, resetField, values],
  );

  const handleDeleteUser = useCallback(() => {
    onDeleteUser(user, onFinish);
  }, [user, onDeleteUser, onFinish]);

  const {
    departmentOptions,
    rotationOptions,
    professionOptions,
    roleOptions,
    levelOftrainingOptions,
    divisionOptions,
    specialtyOptions,
    unitOptions,
    fieldLabels,
    fieldHasOptions,
  } = useProfileOptions({
    profession: values.profession,
    role: values.role,
    department: values.department,
  });

  const fieldLabel = (key, fallback) => fieldLabels?.[key] || fallback;

  useEffect(() => {
    if (!values.profession || !professional?.profession) {
      return;
    }
    if (values.profession !== professional?.profession) {
      setFieldValue('role', '');
      setFieldValue('levelOfTraining', '');
    }
  }, [values.profession, professional?.profession, setFieldValue]);

  const showMultipleEmailField = isNewUser && _.isArray(values.email);
  const showSimpleEmailField = !isNewUser && !isBulkUpdate && values.email?.length === 1;
  const isBulkAdd = isNewUser && _.isArray(values.email) && values.email?.length > 1;

  const editInUserManagementLinkIfAdmin = message => {
    const [first, second] = message.split(', ');

    return isAccountAdmin ? (
      <>
        {first},&nbsp;
        <Link
          to={`${ROUTES.USERS_MANAGEMENT}?${INTERNAL_ROUTING.QUERY_PARAMS.USERS}=${user?.id}`}
          className={styles.link}>
          {second}
        </Link>
      </>
    ) : null;
  };

  const isWsRestrictionEnabled = useSelector(isWorkspaceRestrictionsEnabled);
  const workspaces = useSelector(getWorkspaces);

  const restrictedWorkspacesOptions = useMemo(() => {
    return workspaces
      .filter(workspace => workspace.restricted)
      .sort((a, b) => sortByAlphabet(a.name, b.name))
      .map(({ id, name }) => ({ value: id, label: name }));
  }, [workspaces]);

  const hasRestrictedWorkspaces = isWsRestrictionEnabled && restrictedWorkspacesOptions.length > 0;

  const userAsArray = Array.isArray(user) ? user : [user];
  const isWsAccessUpdateForCollaborators = isNewUser
    ? false
    : userAsArray.some(user => user?.permissions !== USER_PERMISSIONS.VIEWER);

  const filterAllowedValues = ({ options, values }) => {
    if (!values) {
      return [];
    }
    const optionsIds = options.map(({ value }) => value);

    return values.filter(id => optionsIds.includes(id));
  };

  const renderRestrictedWsSelector = () => {
    if (!hasRestrictedWorkspaces) {
      return null;
    }

    const selectorComponent = (
      <SelectBox
        allowClear
        mode="multiple"
        label={lang.RESTRICTED_WORKSPACE}
        placeholder={lang.RESTRICTED_WORKSPACE_PLACEHOLDER}
        name="restrictedWorkspaces"
        onChange={setFieldValue}
        options={restrictedWorkspacesOptions}
        value={filterAllowedValues({
          options: restrictedWorkspacesOptions,
          values: values.restrictedWorkspaces,
        })}
        error={errors.restrictedWorkspaces}
        disabled={isWsAccessUpdateForCollaborators || !canEdit}
      />
    );

    return isWsAccessUpdateForCollaborators ? (
      <Tooltip placement={'top'} title={lang.RESTRICTED_WORKSPACE_DISCLAIMER}>
        {selectorComponent}
      </Tooltip>
    ) : (
      selectorComponent
    );
  };

  const showDeactivationDate = e => {
    if (e.target.checked) {
      setFieldValue('deactivationDate', new Date().toISOString());
    } else {
      setFieldValue('deactivationDate', null);
    }
  };

  return (
    <div className={classNames(['new-design-system', styles.form])}>
      <div className={styles.fields}>
        {showMultipleEmailField && (
          <SelectBox
            data-testid="user-add-form-email"
            label={lang.USERS_EMAIL}
            placeholder={lang.USERS_EMAIL}
            name="email"
            value={values.email}
            error={errors.email}
            onChange={(_name, emails) => setEmails(emails)}
            mode="tags"
            options={[]}
            // Keep the dropdown closed — with no options it only renders a stale
            // tag-preview, and an open dropdown swallows Tab from the input.
            open={false}
            tokenSeparators={[',', ' ', ';']}
            onInputKeyDown={e => {
              // antd's tags-mode Select drops the typed text on Tab — commit it ourselves first.
              if (e.key !== 'Tab' && e.key !== 'Enter') return;
              const text = e.target.value?.trim();
              if (!text || values.email?.includes(text)) return;
              setEmails([...(values.email || []), text]);
            }}
            required
          />
        )}
        {showSimpleEmailField && (
          <TextInput
            label={lang.USERS_EMAIL}
            placeholder={lang.USERS_EMAIL}
            name="email[0]"
            value={values.email[0]}
            error={errors.email}
            touched={touched.email}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={true}
          />
        )}
        {(isBulkUpdate || isBulkAdd) && (
          <Tag color="magenta" className={styles.note}>
            {isBulkAdd ? lang.MULTIPLE_USERS_ADD_WARNING : lang.MULTIPLE_USERS_EDIT_WARNING}
          </Tag>
        )}
        <TextInput
          label={lang.FIRST_NAME}
          placeholder={lang.FIRST_NAME}
          name="firstName"
          value={values.firstName}
          error={errors.firstName}
          touched={touched.firstName}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isBulkUpdate || isBulkAdd || !canEdit}
        />
        <TextInput
          label={lang.LAST_NAME}
          placeholder={lang.LAST_NAME}
          name="lastName"
          value={values.lastName}
          error={errors.lastName}
          touched={touched.lastName}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isBulkUpdate || isBulkAdd || !canEdit}
        />
        <TextInput
          allowClear
          label={lang.PHONE}
          placeholder={lang.PHONE}
          name="phone"
          value={values.phone}
          error={errors.phone}
          touched={touched.phone}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isBulkUpdate || isBulkAdd || !canEdit}
        />
        <TextInput
          allowClear
          label={lang.PAGER}
          placeholder={lang.PAGER}
          name="pager"
          value={values.pager}
          error={errors.pager}
          touched={touched.pager}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isBulkUpdate || isBulkAdd || !canEdit}
        />
        <TextInput
          allowClear
          label={lang.CISCO}
          placeholder={lang.CISCO}
          name="cisco"
          value={values.cisco}
          error={errors.cisco}
          touched={touched.cisco}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isBulkUpdate || isBulkAdd || !canEdit}
        />
        {fieldHasOptions.department && (
          <SelectBox
            label={fieldLabel('department', lang.DEPARTMENT)}
            placeholder={fieldLabel('department', lang.DEPARTMENT)}
            name="department"
            value={values.department}
            error={errors.department}
            onChange={setFieldValue}
            options={departmentOptions}
            disabled={!departmentOptions || !canEdit}
          />
        )}
        {fieldHasOptions.profession && (
          <SelectBox
            label={fieldLabel('profession', lang.PROFESSION)}
            placeholder={fieldLabel('profession', lang.PROFESSION)}
            name="profession"
            value={values.profession}
            error={errors.profession}
            onChange={(field, value) => handleDependentFieldChange('profession', 'role', value)}
            options={professionOptions}
            disabled={!canEdit}
          />
        )}
        {fieldHasOptions.role && (
          <SelectBox
            label={fieldLabel('role', lang.ROLE)}
            placeholder={fieldLabel('role', lang.ROLE)}
            name="role"
            value={values.role}
            error={errors.role}
            onChange={(field, value) =>
              handleDependentFieldChange('role', 'levelOfTraining', value)
            }
            options={roleOptions ?? []}
            disabled={!roleOptions?.length || !canEdit}
            required={professionsWithRoles.has(values.profession)}
          />
        )}
        {fieldHasOptions.levelOfTraining && (
          <SelectBox
            label={fieldLabel('levelOfTraining', lang.LEVEL_OF_TRAINING)}
            placeholder={fieldLabel('levelOfTraining', lang.LEVEL_OF_TRAINING)}
            name="levelOfTraining"
            value={values.levelOfTraining}
            error={errors.levelOfTraining}
            onChange={setFieldValue}
            options={levelOftrainingOptions ?? []}
            disabled={!levelOftrainingOptions?.length || !canEdit}
            required={rolesWithLevels.has(values.role)}
          />
        )}
        <SelectBox
          allowClear
          key="rotation"
          label={lang.ROTATION}
          placeholder={lang.ROTATION}
          name="rotation"
          value={values.rotation}
          error={errors.rotation}
          onChange={setFieldValue}
          options={rotationOptions}
          disabled={!rotationOptions || !canEdit}
        />
        {fieldHasOptions.division && (
          <SelectBox
            key="division"
            allowClear
            mode="multiple"
            label={fieldLabel('division', lang.DIVISION)}
            placeholder={fieldLabel('division', lang.DIVISION)}
            name="division"
            onChange={setFieldValue}
            options={divisionOptions}
            value={values.division}
            disabled={!divisionOptions || !canEdit}
          />
        )}
        <LabelsSelector
          allowClear
          value={values.location}
          error={errors.location}
          placeholder={lang.LOCATION}
          label={lang.LOCATION}
          name="location"
          onChange={setFieldValue}
          wrapperClass={styles.location}
          disabled={isBulkUpdate || isBulkAdd || !canEdit}
        />
        {fieldHasOptions.specialty && (
          <SelectBox
            allowClear
            mode="multiple"
            label={fieldLabel('specialty', lang.SPECIALTY)}
            placeholder={fieldLabel('specialty', lang.SPECIALTY)}
            name="specialty"
            onChange={setFieldValue}
            options={specialtyOptions}
            value={values.specialty}
            error={errors.specialty}
            disabled={!specialtyOptions || !canEdit}
          />
        )}
        {fieldHasOptions.unit && (
          <SelectBox
            allowClear
            mode="multiple"
            label={fieldLabel('unit', lang.UNIT)}
            placeholder={fieldLabel('unit', lang.UNIT)}
            name="unit"
            value={values.unit}
            error={errors.unit}
            onChange={setFieldValue}
            options={unitOptions}
            disabled={!canEdit}
          />
        )}
        <TextInput
          allowClear
          label={lang.TITLE}
          placeholder={lang.TITLE}
          name="title"
          value={values.title}
          error={errors.title}
          touched={touched.title}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isBulkUpdate || isBulkAdd || !canEdit}
        />
        <Tooltip
          title={isSsoEnforced && !isNewUser ? lang.SSO_PERMISSIONS_MANAGED_TOOLTIP : ''}
          placement="topLeft">
          <SelectBox
            label={lang.PERMISSIONS}
            placeholder={lang.PERMISSIONS}
            name="permissions"
            value={values.permissions}
            error={errors.permissions}
            onChange={setFieldValue}
            options={permissionsOptions}
            disabled={
              (isBulkUpdate && !allowToBulkUpdatePermissions) ||
              isBulkAdd ||
              !permissionsOptions ||
              !canEdit ||
              (isSsoEnforced && !isNewUser)
            }
            required={!isBulkUpdate}
          />
        </Tooltip>
        {isSingleUserEdit && isQualityMetricsVisible && (
          <fieldset className={styles.providerNames}>
            <legend className={styles.providerNamesLegend}>{lang.PROVIDER_NAMES}</legend>
            <p className={styles.providerNamesHint}>{lang.PROVIDER_NAMES_ROOT_ONLY}</p>
            <Button
              htmlType="button"
              className={styles.providerNamesAddButton}
              onClick={openAddProviderName}
              disabled={!canEdit}>
              {lang.PROVIDER_NAMES_ADD}
            </Button>
            {providerNames.length > 0 && (
              <table className={styles.providerNamesTable}>
                <thead>
                  <tr>
                    <th scope="col">{lang.PROVIDER_NAMES_TABLE_PROVIDER}</th>
                    <th scope="col">{lang.PROVIDER_NAMES_TABLE_NAME}</th>
                    <th>
                      <span className="sr-only">{lang.PROVIDER_NAMES_TABLE_ACTIONS}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {providerNames.map(entry => (
                    <tr key={providerNameId(entry)}>
                      <td>{getProviderLabel(entry.provider)}</td>
                      <td>{entry.value}</td>
                      <td>
                        <IconButton
                          label={lang.PROVIDER_NAMES_REMOVE?.replace('{name}', entry.value)}
                          onClick={() => removeProviderName(entry)}
                          disabled={!canEdit}>
                          <FontAwesomeIcon icon={faXmark} />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {providerNames.length === 0 && (
              <p className={styles.providerNamesEmpty}>{lang.PROVIDER_NAMES_EMPTY}</p>
            )}
            {providerNamesError && (
              <span className={styles.providerNamesError} role="alert">
                {providerNamesError}
              </span>
            )}
            <Modal
              open={isAddProviderOpen}
              title={lang.PROVIDER_NAMES_ADD}
              onCancel={closeAddProviderName}
              onSubmit={addProviderName}
              submitText={lang.PROVIDER_NAMES_ADD_CONFIRM}
              cancelText={lang.CANCEL}
              submitDisabled={!newName.trim()}>
              <SelectBox
                label={lang.PROVIDER_NAMES_PROVIDER}
                name="providerNamesProvider"
                value={newNameProvider}
                onChange={(_name, provider) => setNewNameProvider(provider ?? DEFAULT_PROVIDER)}
                options={PROVIDER_OPTIONS}
                sortOptions={false}
              />
              <TextInput
                label={lang.PROVIDER_NAMES_NAME}
                placeholder={lang.PROVIDER_NAMES_PLACEHOLDER?.replace(
                  '{provider}',
                  getProviderLabel(newNameProvider),
                )}
                name="providerNameDraft"
                value={newName}
                maxLength={PROVIDER_IDENTIFICATION_VALUE_MAX}
                onChange={e => setNewName(e.target.value)}
                // Enter adds the name instead of submitting the drawer behind the dialog.
                onKeyDown={e => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  addProviderName();
                }}
              />
            </Modal>
          </fieldset>
        )}
        {renderRestrictedWsSelector()}
        <Checkbox onChange={showDeactivationDate} defaultChecked={!!values.deactivationDate}>
          {lang.REVOKE_ACCESS}
        </Checkbox>
        {values.deactivationDate && (
          <DatePicker
            label={lang.DEACTIVATION_DATE_LABEL}
            name="deactivationDate"
            value={values.deactivationDate}
            error={errors.deactivationDate}
            touched={touched.deactivationDate}
            onChange={v => setFieldValue('deactivationDate', v)}
          />
        )}
      </div>

      {canEdit ? (
        <>
          {error && <Alert className={styles.error} type="error" showIcon message={error} />}
          <div className={styles.buttonsContainer}>
            <Button
              data-testid="user-add-form-submit"
              onClick={handleSubmit}
              type="primary"
              size="large"
              disabled={!isValid}
              loading={isLoading}>
              {isNewUser && !isBulkUpdate ? lang.ADD_NEW_USER : lang.SAVE}
            </Button>
            {!isNewUser && !isBulkUpdate && (
              <Button onClick={handleDeleteUser} type="danger" size="large" disabled={isLoading}>
                {lang.DELETE}
              </Button>
            )}
          </div>
        </>
      ) : (
        <>{editInUserManagementLinkIfAdmin(lang.CANNOT_EDIT_HERE)}</>
      )}
    </div>
  );
});

export { UserDetailsFormComponent };
